/**
 * UI-3: useInvestigation Hook
 *
 * Replaces useEntityTimeline. Fetches events, derives current state,
 * then produces a focused InvestigationContext — NOT raw event data.
 *
 * The consumer gets: situation, recent activity (time-windowed),
 * anomalies, impact signals, and recommended actions.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type {
  TimelineEvent,
  RoomState,
  GuestState,
  RequestState,
} from '../services/entityEventTypes';
import {
  fetchRoomEvents,
  fetchGuestEvents,
  fetchRequestEvents,
} from '../services/entityEventService';
import { fetchGuestRequests } from '../../api';
import {
  replayRoomState,
  replayGuestState,
  replayRequestState,
} from '../services/entityReplayEngine';
import type { InvestigationContext } from '../services/investigationEngine';
import {
  buildRoomInvestigation,
  buildGuestInvestigation,
  buildRequestInvestigation,
} from '../services/investigationEngine';

interface UseInvestigationOptions {
  entityType: 'room' | 'guest' | 'request';
  entityId: string;
  windowMinutes?: number;
}

export interface InvestigationResult {
  investigation: InvestigationContext | null;
  roomState: RoomState | null;
  guestState: GuestState | null;
  requestState: RequestState | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useInvestigation({
  entityType,
  entityId,
  windowMinutes = 120,
}: UseInvestigationOptions): InvestigationResult {
  const [investigation, setInvestigation] = useState<InvestigationContext | null>(null);
  const [roomState, setRoomState] = useState<RoomState | null>(null);
  const [guestState, setGuestState] = useState<GuestState | null>(null);
  const [requestState, setRequestState] = useState<RequestState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const currentEntityRef = useRef(`${entityType}:${entityId}`);
  const abortRef = useRef<AbortController | null>(null);
  const hasLoadedRef = useRef(false);

  const load = useCallback(async () => {
    const key = `${entityType}:${entityId}`;
    currentEntityRef.current = key;

    // Abort any in-flight request
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    // Only show skeleton on initial load — not on polls or manual refresh
    if (!hasLoadedRef.current) {
      setLoading(true);
    }
    setError(null);

    try {
      // Fetch events (limited — we only need enough for replay + investigation)
      let result: { events: readonly TimelineEvent[] };

      switch (entityType) {
        case 'room':
          result = await fetchRoomEvents(entityId, 100);
          break;
        case 'guest':
          result = await fetchGuestEvents(entityId, 100);
          break;
        case 'request':
          result = await fetchRequestEvents(entityId, 100);
          break;
      }

      if (currentEntityRef.current !== key || controller.signal.aborted) return;

      const events = result.events;

      // Replay to get current state, then build investigation
      switch (entityType) {
        case 'room': {
          const state = replayRoomState(events);
          setRoomState(state);
          setGuestState(null);
          setRequestState(null);
          setInvestigation(buildRoomInvestigation(events, state, windowMinutes));
          break;
        }
        case 'guest': {
          const state = replayGuestState(entityId, events);
          setGuestState(state);
          setRoomState(null);
          setRequestState(null);
          setInvestigation(buildGuestInvestigation(events, state, windowMinutes));
          break;
        }
        case 'request': {
          const replayedState = replayRequestState(entityId, events);
          // Override status with authoritative value from the store — ledger replay
          // may lag if a status event type was not yet in the schema when the
          // transition was recorded (e.g. IN_PROGRESS recorded as REQUEST_ASSIGNED).
          const allRequests = await fetchGuestRequests().catch(() => []);
          const liveRequest = allRequests.find(r => r.id === entityId);
          const state: typeof replayedState = liveRequest
            ? { ...replayedState, status: liveRequest.status as typeof replayedState.status }
            : replayedState;
          setRequestState(state);
          setRoomState(null);
          setGuestState(null);
          setInvestigation(buildRequestInvestigation(events, state, windowMinutes));
          break;
        }
      }
    } catch (err) {
      if (currentEntityRef.current !== key || controller.signal.aborted) return;
      setError(err instanceof Error ? err.message : 'Failed to load investigation');
    } finally {
      if (currentEntityRef.current === `${entityType}:${entityId}` && !controller.signal.aborted) {
        hasLoadedRef.current = true;
        setLoading(false);
      }
    }
  }, [entityType, entityId, windowMinutes]);

  useEffect(() => {
    hasLoadedRef.current = false;
    load();
    const interval = setInterval(load, 30_000);
    return () => {
      clearInterval(interval);
      abortRef.current?.abort();
    };
  }, [load]);

  return {
    investigation,
    roomState,
    guestState,
    requestState,
    loading,
    error,
    refresh: load,
  };
}
